import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import * as CanvasUtils from '../utils/canvas';
import { usePatientDocumentContext } from '../contexts/PatientDocument';

function useSignature({ onAfterSave } = {}) {
	const { innerWidth: width } = window;

	const canvasOptions = useMemo(() => ({
		height: 212,
		width: width > 600 ? 440 : width,
		className: 'signature-canvas'
	}), [width]);

	const { document, code } = usePatientDocumentContext();

	const defaultSignatureOptions = {
		signature_type: document?.signature_type || 'TEXT',
		signature_text: document?.signature_text || document?.destinatary?.name || ''
	};

	const [signatureCanvas, setSignatureCanvas] = useState();
	const [signatureOptions, setSignatureOptions] = useState(defaultSignatureOptions);
	const [loading, setLoading] = useState(true);

	const getCanvasRef = useCallback(ref => setSignatureCanvas(ref), []);
	const textCanvasRef = useRef(null);

	useEffect(() => {
		setTimeout(() => setLoading(false), 400);
	}, [loading]);

	useEffect(() => {
		if (document?.signature_url && document?.signature_type === 'CANVAS') {
			signatureCanvas?.fromDataURL(document.signature_url, canvasOptions);
		}
	}, [signatureCanvas, canvasOptions, document, document?.signature_url, signatureOptions.signature_type]);

	const saveText = useCallback(canvas => {
		const context = canvas.getContext('2d');

		CanvasUtils.mountCanvasText({
			text: signatureOptions.signature_text,
			canvas: context,
			width: canvas.width,
			lineHeight: 42,
		});

		document.signature_url = canvas.toDataURL('image/png');
		document.signature_text = signatureOptions.signature_text;
	}, [document, signatureOptions.signature_text]);

	const saveCanvas = useCallback(canvas => {
		document.signature_url = canvas.toDataURL('image/png');
	}, [document]);

	const save = useCallback(() => {
		const isTextSignature = signatureOptions.signature_type === 'TEXT';

		if ((signatureCanvas?.isEmpty() && !isTextSignature) || (isTextSignature && !signatureOptions.signature_text.length)) {
			return toast.warn('É necessário assinar o documento para salvar');
		}

		if (isTextSignature) {
			saveText(textCanvasRef.current);
		} else {
			saveCanvas(signatureCanvas);
		}

		document.signature_type = signatureOptions.signature_type;

		if (onAfterSave) {
			onAfterSave();
		}
	}, [document, signatureCanvas, signatureOptions, textCanvasRef, saveCanvas, saveText, onAfterSave]);

	const clearCanvas = useCallback(() => signatureCanvas?.clear(), [signatureCanvas]);

	const clearText = useCallback(() => {
		setSignatureOptions(current => ({
			signature_type: current.signature_type,
			signature_text: ''
		}));
	}, []);

	const clear = useCallback(() => {
		if (signatureOptions.signature_type === 'CANVAS') {
			return clearCanvas();
		}
		return clearText();
	}, [signatureOptions, clearCanvas, clearText]);

	const onChangeSignatureType = useCallback(type => {
		delete document.signature_url;

		setSignatureOptions(current => ({
			signature_type: type,
			signature_text: current.signature_text
		}));
	}, [document]);

	const onChangeSignatureText = useCallback(event => {
		setSignatureOptions(current => ({
			signature_type: current.signature_type,
			signature_text: event.target.value
		}));
	}, []);

	const handleKeyDown = useCallback(event => {
		if (event.key === 'Enter') {
			event.preventDefault();
			return;
		}
		onChangeSignatureText(event);
	}, [onChangeSignatureText]);

	const undo = useCallback(() => {
		if (signatureCanvas?.isEmpty()) return;

		const newCanvas = signatureCanvas.toData();
		newCanvas.pop();
		signatureCanvas.fromData(newCanvas);
	}, [signatureCanvas]);

	return {
		loading,
		canvasOptions,
		signatureOptions,
		getCanvasRef,
		textCanvasRef,
		save,
		clear,
		undo,
		onChangeSignatureType,
		onChangeSignatureText,
		handleKeyDown,
		document,
		code
	};
}

export default useSignature;
